/* Session-timeout warning — mounts inside RequireAuth. Reads the
 * current session's `session_expires_at` from `useAuth().user` and:
 *
 *   1. Sleeps until 5 min before expiry.
 *   2. Opens a modal with a live mm:ss countdown + two actions:
 *        • "Extend session" → POST /api/auth/extend, then refresh().
 *        • "Log out"        → normal logout.
 *   3. Auto-refreshes `/auth/me` again after any extend so subsequent
 *      timers pick up the new expiry.
 *
 * The backend session TTL is 7 days with a server-side sliding refresh
 * at < 3.5 days remaining, so in practice this dialog only fires when
 * a tab has been idle for ~6 days 23 hours 55 min — but the "Extend"
 * button works at any time.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, TimerReset, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth_context';
import { authApi } from '@/lib/auth_api';

const WARN_BEFORE_MS = 5 * 60 * 1000; // 5 minutes

function fmt(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

export default function SessionTimeoutWarning() {
  const { user, refresh, logout } = useAuth();
  const [now, setNow]         = useState(() => Date.now());
  const [extending, setExtending] = useState(false);

  // Parse expiry timestamp — invalid / missing → disable component.
  const expiresAt = useMemo(() => {
    const raw = user?.session_expires_at;
    if (!raw) return null;
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? null : t;
  }, [user]);

  // Tick every 15 s. Fine-grained enough for the mm:ss countdown, cheap.
  useEffect(() => {
    if (!expiresAt) return undefined;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  if (!expiresAt) return null;

  const remaining = expiresAt - now;
  const openDialog = remaining > 0 && remaining <= WARN_BEFORE_MS;

  const handleExtend = async () => {
    setExtending(true);
    try {
      await authApi.extend();
      await refresh();
      toast.success('Session extended — you\u2019re good for another 7 days.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not extend session.');
    } finally {
      setExtending(false);
    }
  };

  return (
    <Dialog open={openDialog} onOpenChange={() => { /* modal — no dismiss */ }}>
      <DialogContent className="max-w-md" data-testid="session-timeout-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-[15px]">
            <TimerReset className="h-4 w-4 text-primary" />
            Your session is about to expire
          </DialogTitle>
          <DialogDescription>
            To keep your work safe we\u2019re about to sign you out. Tap
            <em> Extend session </em> to stay signed in — otherwise you\u2019ll
            be logged out when the timer hits zero.
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 flex items-center justify-center">
          <div
            className="rounded-md border border-border bg-card px-4 py-2 text-center font-mono text-2xl tabular-nums"
            data-testid="session-timeout-countdown"
          >
            {fmt(remaining)}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={logout}
            data-testid="session-timeout-logout"
            className="gap-1.5"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </Button>
          <Button
            onClick={handleExtend}
            disabled={extending}
            data-testid="session-timeout-extend"
            className="gap-1.5"
          >
            {extending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <TimerReset className="h-3.5 w-3.5" />}
            Extend session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
